import { GetStaticPaths, GetStaticProps } from 'next'
import Image from 'next/image'
import clsx from 'clsx'

import Link from '@/atoms/Link/Link'
import ResourceForm from '@/molecules/Forms/ResourceForm/ResourceForm'
import Layout from '@/templates/Layout/Layout'
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { resourceDetailsQuery } from '@/utils/queries/resources'
import { constructBreadcrumbJsonSchema } from '@/utils/functions/jsonSchema'
import { INSTANT_REVALIDATION } from '@/utils/constants/revalidation'
import { capture404PageError } from '@/utils/functions/elk-server'

/* styling */
import styles from '@/styles/resourcesDetails.module.css'

const RESOURCE_DETAILS_REVALIDATE_TIME = 60 * 10 // 10 minutes

interface Props {
  page: ResourceDetailsPageAttributes
}

function ResourceDetailsPage({ page }: Props) {
  const url = `https://www.peppercontent.io/resources/${page.type}/${page.slug}/`

  const metadata = [
    { property: 'og:title', content: page.seo.title },
    { property: 'description', content: page.seo.description },
    { property: 'og:description', content: page.seo.description },
    {
      property: 'og:image',
      content: page.seo.featuredImage.data?.attributes.url
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url }
  ]

  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home', url: '/' },
    { name: 'Resources', url: '/resources/' },
    { name: page.title, url: `/blog/${page.type}/${page.slug}` }
  ]

  return (
    <Layout
      title={page.seo.title}
      canonical={`/resources/${page.type}/${page.slug}/`}
      meta={metadata}
      jsonSchema={[constructBreadcrumbJsonSchema(breadcrumbs)]}
    >
      <section className="relative">
        <div className="max-width-container  |  grid lg:grid-cols-2 items-center">
          <div className="max-w-md  |  mx-auto lg:mx-0 py-24">
            <Link
              href="/resources/"
              className="text-small text-gray-70  |  inline-block  |  mb-4"
            >
              Back to All Resources
            </Link>
            <h1 className="h2 font-medium  |  mb-4">{page.heroTitle}</h1>
            <div
              className="text-paragraph-1 text-gray-70  |  mb-10"
              dangerouslySetInnerHTML={{ __html: page.heroDescription }}
            />
            <a href="#resource-form" className="btn-primary  |  h-12 w-full">
              {page.heroCta}
            </a>
          </div>
          <div className="py-8">
            <div
              className={clsx('max-w-[360px] mx-auto', styles.imageContainer)}
            >
              <Image
                src={page.heroImage.data.attributes.url}
                alt={page.heroImage.data.attributes.alternativeText}
                width={page.heroImage.data.attributes.width}
                height={page.heroImage.data.attributes.height}
              />
            </div>
          </div>
        </div>

        <section className="absolute top-0 z-[-1]  |  grid lg:grid-cols-2  |  h-full w-full">
          <div className="bg-yellow-10"></div>
          <div style={{ backgroundColor: page.featuredHexcode }}></div>
        </section>
      </section>

      <section className="max-width-container  |  py-20">
        <div
          className={clsx(styles.pageDetails, 'max-w-3xl mx-auto')}
          dangerouslySetInnerHTML={{ __html: page.pageDetails }}
        />
      </section>

      {/* TODO:Remove pardotUrl field from strapi */}
      <section className="max-width-container  |  max-w-3xl  |  md:mb-16 px-0 md:px-6">
        <ResourceForm
          formName={page.title}
          title={page.formTitle}
          description={page.formDescription}
          ctaTitle={page.formCta}
          successMessage={page.formSuccessMessage}
        />
      </section>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // if slug is not following `/resources/${type}/${slug}` format, return 404
  if (params?.slug?.length !== 2) return { notFound: true }

  const resourceType = params.slug[0]
  const resourceSlug = params.slug[1]

  const result = await getStrapiGraphqlData<
    ResourceDetailsPageQuery | undefined
  >(resourceDetailsQuery.loc?.source.body, {
    slug: resourceSlug,
    type: resourceType
  })

  // if api returns no data, return 404
  if (!result || result.resources.data.length === 0) {
    capture404PageError(`/resources/${resourceType}/${resourceSlug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  return {
    props: {
      page: result.resources.data[0].attributes
    },
    revalidate: RESOURCE_DETAILS_REVALIDATE_TIME
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export default ResourceDetailsPage
